import React from 'react';
import { TRANSLATIONS } from '../constants';
import { useSmartBack } from '../App';
import { formatLocalizedDate } from '../utils/gameUtils';

const LAST_UPDATE_DATE = '2026-02-12T00:00:00Z';

export const InfoPages: React.FC<{ type: 'about' | 'guide' | 'privacy' | 'terms' | 'contact' }> = ({ type }) => {
  const { handleBack, lang } = useSmartBack();
  const localizedDate = formatLocalizedDate(LAST_UPDATE_DATE, lang);
  const lastUpdatedLabel = TRANSLATIONS.lastUpdated[lang];

  const LastUpdatedHeader = () => (
    <div className="flex items-center gap-4 mb-2">
      <div className="h-[2px] w-8 bg-prestige-gold rounded-full" />
      <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 dark:text-zinc-400">
        {lastUpdatedLabel}: {localizedDate}
      </span>
    </div>
  );

  const renderContent = () => {
    switch (type) {
      case 'about':
        return (
          <div className="space-y-8 lg:space-y-7 animate-fade-in">
            <section className="space-y-3">
              <LastUpdatedHeader />
              <h2 className="text-4xl sm:text-5xl lg:text-4xl font-heading uppercase tracking-widest text-black dark:text-white leading-none">
                {TRANSLATIONS.aboutMissionTitle[lang]}
              </h2>
              <p className="text-lg sm:text-xl lg:text-lg leading-relaxed text-zinc-900 dark:text-zinc-200 font-medium italic border-l-4 border-prestige-gold pl-6 py-1">
                {TRANSLATIONS.aboutMissionContent[lang]}
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.aboutStoryTitle[lang]}
              </h2>
              <p className="text-zinc-900 dark:text-zinc-200 text-sm leading-relaxed max-w-2xl">
                {TRANSLATIONS.aboutStoryContent[lang]}
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.aboutLogicTitle[lang]}
              </h2>
              <div className="bg-zinc-100 dark:bg-zinc-900/40 p-8 lg:p-6 rounded-3xl border border-zinc-200 dark:border-zinc-800">
                <p className="text-zinc-800 dark:text-zinc-100 text-sm leading-relaxed font-semibold">
                  {TRANSLATIONS.aboutLogicContent[lang]}
                </p>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.aboutCommitmentTitle[lang]}
              </h2>
              <p className="text-zinc-900 dark:text-zinc-200 text-sm leading-relaxed">
                {TRANSLATIONS.aboutCommitmentContent[lang]}
              </p>
            </section>
            
            <div className="bg-zinc-50 dark:bg-zinc-950 p-8 lg:p-6 rounded-[32px] flex flex-col items-center gap-6 lg:gap-4 border border-zinc-200 dark:border-zinc-900 text-center shadow-inner mt-4">
              <img 
                src="https://www.themoviedb.org/assets/2/v4/logos/v2/blue_short-8e7b30f73a4020692ccca9c88bafe5dcb6f8a62a4c6bc55cd9ba82bb2cd95f6c.svg" 
                alt="TMDB Official Logo" 
                className="h-6 opacity-80 hover:opacity-100 transition-opacity"
              />
              <p className="text-[9px] sm:text-[10px] leading-relaxed text-zinc-500 dark:text-zinc-400 font-black uppercase tracking-[0.25em] max-w-sm mx-auto">
                {TRANSLATIONS.tmdbDisclaimer[lang]}
              </p>
            </div>
          </div>
        );
      case 'privacy':
        return (
          <div className="space-y-8 lg:space-y-7 animate-fade-in">
            <LastUpdatedHeader />
            <h1 className="text-5xl sm:text-6xl lg:text-5xl font-heading uppercase tracking-widest text-black dark:text-white mb-8 lg:mb-6 leading-none">
              {TRANSLATIONS.privacyTitle[lang]}
            </h1>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.privacyPhilosophyTitle[lang]}
              </h2>
              <p className="text-zinc-900 dark:text-zinc-200 leading-relaxed font-medium">
                {TRANSLATIONS.privacyPhilosophyContent[lang]}
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.privacyDataTitle[lang]}
              </h2>
              <p className="text-zinc-900 dark:text-zinc-200 text-sm leading-relaxed mb-2">
                {TRANSLATIONS.privacyDataContent[lang]}
              </p>
              <div className="bg-zinc-100 dark:bg-zinc-900/40 p-8 lg:p-6 rounded-3xl border border-zinc-200 dark:border-zinc-800">
                <p className="text-prestige-gold text-xs font-black uppercase tracking-widest leading-normal">
                  {TRANSLATIONS.privacyDataControl[lang]}
                </p>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.privacyTmdbTitle[lang]}
              </h2>
              <p className="text-zinc-900 dark:text-zinc-200 text-sm leading-relaxed">
                {TRANSLATIONS.privacyTmdbContent[lang]}
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.privacyGdprTitle[lang]}
              </h2>
              <p className="text-zinc-900 dark:text-zinc-200 text-sm leading-relaxed">
                {TRANSLATIONS.privacyGdprContent[lang]}
              </p>
            </section>

            <section className="space-y-3 text-center pt-8 border-t border-zinc-200 dark:border-zinc-800">
              <h2 className="text-xl lg:text-lg font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.privacyInquiriesTitle[lang]}
              </h2>
              <p className="text-zinc-600 dark:text-zinc-400 text-xs font-bold tracking-wider">
                {TRANSLATIONS.privacyInquiriesContent[lang]}
              </p>
            </section>
          </div>
        );
      case 'terms':
        return (
          <div className="space-y-8 lg:space-y-7 animate-fade-in">
            <LastUpdatedHeader />
            <h1 className="text-5xl sm:text-6xl lg:text-5xl font-heading uppercase tracking-widest text-black dark:text-white mb-8 lg:mb-6 leading-none">
              {TRANSLATIONS.termsTitle[lang]}
            </h1>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.termsSection1Title[lang]}
              </h2>
              <p className="text-zinc-900 dark:text-zinc-200 leading-relaxed font-medium italic border-l-2 border-zinc-800 dark:border-zinc-700 pl-6 py-1">
                {TRANSLATIONS.termsSection1Content[lang]}
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.termsSection2Title[lang]}
              </h2>
              <div className="bg-zinc-100 dark:bg-zinc-900/40 p-8 lg:p-6 rounded-3xl border border-zinc-200 dark:border-zinc-800">
                <p className="text-zinc-800 dark:text-zinc-100 text-sm leading-relaxed">
                  {TRANSLATIONS.termsSection2Content[lang]}
                </p>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.termsSection3Title[lang]}
              </h2>
              <p className="text-zinc-900 dark:text-zinc-200 text-sm leading-relaxed">
                {TRANSLATIONS.termsSection3Content[lang]}
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl lg:text-xl font-heading uppercase tracking-widest text-black dark:text-white">
                {TRANSLATIONS.termsSection4Title[lang]}
              </h2>
              <p className="text-zinc-900 dark:text-zinc-200 text-sm leading-relaxed">
                {TRANSLATIONS.termsSection4Content[lang]}
              </p>
            </section>
            
            <div className="pt-8 border-t border-zinc-200 dark:border-zinc-800">
               <p className="text-center text-[9px] font-black uppercase tracking-[0.4em] text-zinc-500 dark:text-zinc-600">
                 © 2026 Movie Riddle
               </p>
            </div>
          </div>
        );
      case 'guide':
        return (
          <div className="space-y-8 lg:space-y-7 animate-fade-in text-left">
            <LastUpdatedHeader />
            <h1 className="text-5xl sm:text-6xl lg:text-5xl font-heading uppercase tracking-widest text-black dark:text-white mb-8 lg:mb-6 leading-none">
              {TRANSLATIONS.guideMainTitle[lang]}
            </h1>

            <section className="space-y-2">
              <h2 className="text-xl lg:text-lg font-heading uppercase tracking-widest text-prestige-gold">{TRANSLATIONS.guideObjectiveTitle[lang]}</h2>
              <p className="text-zinc-900 dark:text-zinc-200 leading-relaxed font-medium">
                {TRANSLATIONS.guideObjectiveContent[lang]}
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl lg:text-lg font-heading uppercase tracking-widest text-prestige-gold">{TRANSLATIONS.guideRevealTitle[lang]}</h2>
              <div className="space-y-3 text-zinc-900 dark:text-zinc-200 text-sm leading-relaxed">
                 <p className="font-bold text-black dark:text-white uppercase tracking-wider">{TRANSLATIONS.guideRevealIntro[lang]}</p>
                 <ul className="space-y-2">
                   <li className="pl-4 border-l-2 border-zinc-300 dark:border-zinc-800">
                      <span className="font-bold text-zinc-950 dark:text-white block mb-0.5">{TRANSLATIONS.guideRevealStep1Label[lang]}</span> {TRANSLATIONS.guideRevealStep1Desc[lang]}
                   </li>
                   <li className="pl-4 border-l-2 border-zinc-300 dark:border-zinc-800">
                      <span className="font-bold text-zinc-950 dark:text-white block mb-0.5">{TRANSLATIONS.guideRevealStep2Label[lang]}</span> {TRANSLATIONS.guideRevealStep2Desc[lang]}
                   </li>
                   <li className="pl-4 border-l-2 border-zinc-300 dark:border-zinc-800">
                      <span className="font-bold text-zinc-950 dark:text-white block mb-0.5">{TRANSLATIONS.guideRevealStep3Label[lang]}</span> {TRANSLATIONS.guideRevealStep3Desc[lang]}
                   </li>
                 </ul>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl lg:text-lg font-heading uppercase tracking-widest text-prestige-gold">{TRANSLATIONS.guideGuessTitle[lang]}</h2>
              <div className="grid gap-2">
                 {TRANSLATIONS.guideGuessSteps[lang].split('\n').map((step, i) => (
                   <div key={i} className="flex gap-4 items-start bg-zinc-100 dark:bg-zinc-900/40 p-4 lg:p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 transition-colors hover:border-prestige-gold/30">
                      <span className="font-heading text-xl text-prestige-gold leading-none">{i+1}.</span>
                      <span className="text-xs font-bold text-zinc-900 dark:text-zinc-200 uppercase tracking-wider leading-normal">{step}</span>
                   </div>
                 ))}
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl lg:text-lg font-heading uppercase tracking-widest text-prestige-gold">{TRANSLATIONS.guideSquaresTitle[lang]}</h2>
              <p className="text-zinc-700 dark:text-zinc-300 text-sm italic">{TRANSLATIONS.guideSquaresIntro[lang]}</p>
              <div className="space-y-2.5">
                 <div className="flex items-center gap-4">
                   <span className="text-2xl filter drop-shadow-[0_0_5px_rgba(16,185,129,0.3)]">🟩</span>
                   <p className="text-xs font-bold text-zinc-900 dark:text-zinc-200 uppercase tracking-widest">{TRANSLATIONS.guideSquaresGreen[lang]}</p>
                 </div>
                 <div className="flex items-center gap-4">
                   <span className="text-2xl filter drop-shadow-[0_0_5px_rgba(239,68,68,0.3)]">🟥</span>
                   <p className="text-xs font-bold text-zinc-900 dark:text-zinc-200 uppercase tracking-widest">{TRANSLATIONS.guideSquaresRed[lang]}</p>
                 </div>
                 <div className="flex items-center gap-4">
                   <span className="text-2xl filter drop-shadow-[0_0_5px_rgba(0,0,0,0.5)]">⬛</span>
                   <p className="text-xs font-bold text-zinc-900 dark:text-zinc-200 uppercase tracking-widest">{TRANSLATIONS.guideSquaresBlack[lang]}</p>
                 </div>
              </div>
              <div className="mt-4 p-5 lg:p-4 bg-prestige-gold/10 border border-prestige-gold/20 rounded-2xl">
                 <p className="text-[10px] lg:text-[9px] font-black uppercase tracking-[0.2em] text-prestige-gold text-center leading-relaxed">
                    {TRANSLATIONS.guideProTip[lang]}
                 </p>
              </div>
            </section>

            <section className="space-y-5">
              <h2 className="text-xl lg:text-lg font-heading uppercase tracking-widest text-prestige-gold">{TRANSLATIONS.guideModesTitle[lang]}</h2>
              <div className="grid gap-4 lg:grid-cols-2">
                 <div className="bg-zinc-50 dark:bg-zinc-900/20 p-5 rounded-2xl border border-zinc-200 dark:border-zinc-800">
                    <h3 className="font-heading text-lg text-black dark:text-white uppercase tracking-widest mb-1">{TRANSLATIONS.guideDailyTitle[lang]}</h3>
                    <p className="text-zinc-900 dark:text-zinc-200 text-xs leading-relaxed">{TRANSLATIONS.guideDailyContent[lang]}</p>
                 </div>
                 <div className="bg-zinc-50 dark:bg-zinc-900/20 p-5 rounded-2xl border border-zinc-200 dark:border-zinc-800">
                    <h3 className="font-heading text-lg text-black dark:text-white uppercase tracking-widest mb-1">{TRANSLATIONS.guidePracticeTitle[lang]}</h3>
                    <p className="text-zinc-900 dark:text-zinc-200 text-xs leading-relaxed">{TRANSLATIONS.guidePracticeContent[lang]}</p>
                 </div>
              </div>
            </section>
          </div>
        );
      case 'contact':
        return (
          <div className="flex-1 flex flex-col items-center justify-center text-center space-y-8 lg:space-y-6 animate-fade-in py-12 lg:py-6">
            <div className="space-y-4 lg:space-y-2">
              <div className="flex flex-col items-center gap-2">
                <LastUpdatedHeader />
                <h1 className="text-6xl sm:text-7xl lg:text-5xl font-heading uppercase tracking-[0.2em] text-black dark:text-white leading-none">
                  {TRANSLATIONS.contact[lang]}
                </h1>
              </div>
            </div>

            <div className="max-w-xl space-y-4 lg:space-y-3">
              <p className="text-lg sm:text-xl lg:text-base font-medium text-zinc-900 dark:text-zinc-100 leading-relaxed italic">
                {TRANSLATIONS.contactIntro[lang]}
              </p>
              
              <p className="text-sm sm:text-base lg:text-sm text-zinc-900 dark:text-zinc-200 leading-relaxed">
                {TRANSLATIONS.contactDescription[lang]}
              </p>

              <div className="py-2 whitespace-pre-line text-xs sm:text-sm lg:text-xs font-bold uppercase tracking-widest text-prestige-gold leading-loose">
                {TRANSLATIONS.contactQuestions[lang]}
              </div>

              <p className="text-sm sm:text-base lg:text-sm text-zinc-900 dark:text-zinc-200 leading-relaxed">
                {TRANSLATIONS.contactCallToAction[lang]}
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 dark:text-zinc-400">
                {TRANSLATIONS.contactEmailLabel[lang]}
              </p>
              <a 
                href="mailto:hello@movieriddle.com" 
                className="text-2xl sm:text-3xl lg:text-2xl font-bold text-black dark:text-white hover:text-prestige-gold transition-colors underline decoration-zinc-800 dark:decoration-zinc-400 underline-offset-8"
              >
                hello@movieriddle.com
              </a>
              <p className="text-[9px] font-bold uppercase tracking-widest text-zinc-600 dark:text-zinc-400 italic">
                {TRANSLATIONS.contactOutro[lang]}
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="w-full max-w-[800px] mx-auto py-16 lg:py-10 px-6 animate-fade-in flex flex-col min-h-[70vh]">
      <div className="mb-8 lg:mb-4 shrink-0">
        <button 
          onClick={handleBack} 
          className="text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-white transition-colors flex items-center gap-2 font-bold uppercase text-[10px] tracking-widest"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" /></svg>
          {TRANSLATIONS.backLabel[lang]}
        </button>
      </div>
      <div className="flex-1 flex flex-col">
        {renderContent()}
      </div>
    </div>
  );
};